/**
 * TASK-APS-007: ParticleSystemManager Class
 *
 * Main manager for fog and dust particle systems.
 * Orchestrates particle updates, camera synchronization, and rendering.
 */

import * as PIXI from 'pixi.js';
import type { FogConfig, DustConfig, CameraState } from '../../types/terrain';
import { createParticlePool, type Particle } from './particle-pool';
import { updateFogParticle } from './fog-particle-updater';
import { updateDustParticle } from './dust-particle-updater';
import {
  createRadialGradientTexture,
  createPointTexture,
} from '../../utils/placeholder-textures';

/**
 * ParticleSystemManager
 *
 * Manages both fog and dust particle systems using PIXI.ParticleContainer
 * for high-performance rendering.
 *
 * Features:
 * - Fixed particle pools (no create/destroy during runtime)
 * - Fog particles: drift and wrap at edges
 * - Dust particles: float upward with sway, fade at top, respawn at bottom
 * - Camera-responsive parallax offset
 */
export class ParticleSystemManager {
  // Note: Using regular Container instead of ParticleContainer because PixiJS 8
  // changed the ParticleContainer API (addChild -> addParticle with different particle format)
  private fogContainer: PIXI.Container;
  private dustContainer: PIXI.Container;
  private fogParticles: Particle[] = [];
  private dustParticles: Particle[] = [];
  private fogConfig: FogConfig;
  private dustConfig: DustConfig;
  private time: number = 0;

  constructor(fogConfig: FogConfig, dustConfig: DustConfig) {
    this.fogConfig = fogConfig;
    this.dustConfig = dustConfig;

    // Create Containers for particle sprites
    // Using regular Container for PixiJS 8 compatibility
    this.fogContainer = new PIXI.Container();
    this.dustContainer = new PIXI.Container();
  }

  /**
   * Initialize particle systems
   * Creates particles and sprites
   */
  async initialize(): Promise<void> {
    // Create placeholder textures
    const fogTexture = createRadialGradientTexture(0xffffff, 64, 64);
    const dustTexture = createPointTexture(0xffffff, 16, 16);

    // Initialize fog particles
    this.fogParticles = createParticlePool(this.fogConfig);
    for (let i = 0; i < this.fogParticles.length; i++) {
      const particle = this.fogParticles[i];
      const sprite = new PIXI.Sprite(fogTexture);

      // Set initial sprite properties
      sprite.anchor.set(0.5);
      sprite.x = particle.x;
      sprite.y = particle.y;
      sprite.scale.set(particle.scale);
      sprite.alpha = particle.alpha;
      sprite.tint = this.fogConfig.tint;

      this.fogContainer.addChild(sprite);
    }

    // Initialize dust particles
    this.dustParticles = createParticlePool(this.dustConfig);
    for (let i = 0; i < this.dustParticles.length; i++) {
      const particle = this.dustParticles[i];
      const sprite = new PIXI.Sprite(dustTexture);

      // Set initial sprite properties
      sprite.anchor.set(0.5);
      sprite.x = particle.x;
      sprite.y = particle.y;
      sprite.scale.set(particle.scale);
      sprite.alpha = particle.alpha;
      sprite.tint = this.dustConfig.tint;

      this.dustContainer.addChild(sprite);
    }
  }

  /**
   * Update particle systems
   *
   * @param deltaTime - Time elapsed since last update (seconds)
   */
  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update fog particles
    for (let i = 0; i < this.fogParticles.length; i++) {
      const particle = this.fogParticles[i];
      const sprite = this.fogContainer.children[i] as PIXI.Sprite;

      // Update particle data
      updateFogParticle(particle, this.fogConfig, deltaTime);

      // Sync sprite position
      sprite.x = particle.x;
      sprite.y = particle.y;
    }

    // Update dust particles
    for (let i = 0; i < this.dustParticles.length; i++) {
      const particle = this.dustParticles[i];
      const sprite = this.dustContainer.children[i] as PIXI.Sprite;

      // Update particle data
      updateDustParticle(particle, this.dustConfig, deltaTime, this.time);

      // Sync sprite position and alpha
      sprite.x = particle.x;
      sprite.y = particle.y;
      sprite.alpha = particle.alpha;
    }
  }

  /**
   * Update particle positions based on camera movement
   * Applies subtle parallax effect
   *
   * @param camera - Current camera state
   */
  updateCameraPosition(camera: CameraState): void {
    // Apply parallax offset to containers (particles move opposite to camera)
    // Use different parallax factors for depth illusion
    const fogParallaxFactor = 0.05; // Fog moves slowly (5% of camera movement)
    const dustParallaxFactor = 0.15; // Dust moves faster (15% of camera movement)

    // Apply offset scaled by zoom
    this.fogContainer.x = -camera.x * fogParallaxFactor * camera.zoom;
    this.fogContainer.y = -camera.y * fogParallaxFactor * camera.zoom;

    this.dustContainer.x = -camera.x * dustParallaxFactor * camera.zoom;
    this.dustContainer.y = -camera.y * dustParallaxFactor * camera.zoom;
  }

  /**
   * Get the fog particle container for adding to scene
   */
  getFogContainer(): PIXI.Container {
    return this.fogContainer;
  }

  /**
   * Get the dust particle container for adding to scene
   */
  getDustContainer(): PIXI.Container {
    return this.dustContainer;
  }

  /**
   * Clean up particle systems
   */
  destroy(): void {
    if (this.fogContainer && !this.fogContainer.destroyed) {
      this.fogContainer.destroy({ children: true });
    }

    if (this.dustContainer && !this.dustContainer.destroyed) {
      this.dustContainer.destroy({ children: true });
    }

    this.fogParticles = [];
    this.dustParticles = [];
  }
}
